import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import { INCOTERM_OPTIONS } from "pages/shipper/RequestForm/components/Service/ServiceFormDefaultOptions";
import { CustomServiceConfirmationQuestion, SERVICE_REQUIREMENT_LABEL_MAPPING } from "helpers/quotationFormHelper";

export default function ServiceConfirmationSumary() {
  const intl = useIntl();
  const id = useParams().id?.toUpperCase();
  const { watch } = useFormContext();

  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const selectedFreightMode: string = watch("service.freight_mode");
  const customServiceQuestions: CustomServiceConfirmationQuestion[] =
    quotationConfigData?.custom_service_confirmation?.[selectedFreightMode] ?? [];

  const serviceSpecifications = Object.entries(quotationConfigData?.service_confirmation?.service_specification ?? {})
    .filter(([, show]) => show)
    .map(([key]) => (key.endsWith("_required") ? key.slice(0, -9) : key));
  const serviceRequirements = Object.entries(quotationConfigData?.service_confirmation?.service_requirement ?? {})
    .filter(([, show]) => show)
    .map(([key]) => (key.endsWith("_required") ? key.slice(0, -9) : key));

  const renderCustomServiceAnswer = (question: CustomServiceConfirmationQuestion, index: number) => {
    const filteredQuestions = question?.multiple_option_choices?.filter((answer) =>
      (watch(`service_confirmation.custom[${index}].value`) ?? []).includes(answer),
    );
    switch (question.question_type) {
      case "multiple_choices":
        return filteredQuestions?.join(", ");
      case "yes/no":
        return watch(`service_confirmation.custom[${index}].value`) === "Yes" ? (
          <FormattedMessage id="general.yes" defaultMessage="Yes" />
        ) : (
          <>
            <FormattedMessage id="general.no" defaultMessage="No" />,{" "}
            {watch(`service_confirmation.custom[${index}].comment`)}
          </>
        );
      case "free_text":
        return watch(`service_confirmation.custom[${index}].value`);
      default:
        return null;
    }
  };

  if (!quotationConfigData?.service_confirmation) return null;

  return (
    <>
      <div className="mb-4 font-weight-bold">
        <FormattedMessage id="shipment-quotes.components.quotes.serviceConfirmation" defaultMessage="Service Confirmation" />
      </div>

      <div className="font-b3 space-y-2">
        {serviceSpecifications.map((value, index) => (
          <Row key={index}>
            <Col className="text-secondary">
              {SERVICE_REQUIREMENT_LABEL_MAPPING[value] ??
                value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
            </Col>
            <Col>
              {watch(`service_confirmation.defaults.${value}.value`) === "true" ? (
                <FormattedMessage id="general.yes" defaultMessage="Yes" />
              ) : (
                <>
                  <FormattedMessage id="general.no" defaultMessage="No" />,{" "}
                  {watch(`service_confirmation.defaults.${value}.comment`)}
                </>
              )}
            </Col>
          </Row>
        ))}

        {serviceRequirements.map((value, index) => (
          <Row key={index}>
            <Col className="text-secondary">
              {SERVICE_REQUIREMENT_LABEL_MAPPING[value] ??
                value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
            </Col>
            <Col>
              {watch(`service_confirmation.defaults.service_type.value`) === "true" ? (
                <FormattedMessage id="general.yes" defaultMessage="Yes" />
              ) : (
                <>
                  <FormattedMessage id="general.no" defaultMessage="No" />,{" "}
                  {watch(`service_confirmation.defaults.service_type.comment`)}
                </>
              )}
            </Col>
          </Row>
        ))}

        {quotationConfigData?.service_confirmation?.incoterm && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage id="quotation-form.incoterm" defaultMessage="Incoterm" />:{" "}
              {
                INCOTERM_OPTIONS(intl).find((option) => option.value === quotationConfigData.service_confirmation?.incoterm)
                  ?.label
              }
            </Col>
            <Col>
              {watch("service_confirmation.defaults.incoterm.value") === "true" ? (
                <FormattedMessage id="general.yes" defaultMessage="Yes" />
              ) : (
                <>
                  {[
                    intl.formatMessage({ id: "general.no", defaultMessage: "No" }),
                    INCOTERM_OPTIONS(intl).find(
                      (option) => option.value === watch("service_confirmation.defaults.incoterm.selected_incoterm"),
                    )?.label,
                    watch("service_confirmation.defaults.incoterm.comment"),
                  ].join(", ")}
                </>
              )}
            </Col>
          </Row>
        )}

        {customServiceQuestions.map((question, index) => (
          <Row key={index}>
            <Col className="text-secondary">{question.question}</Col>
            <Col>{renderCustomServiceAnswer(question, index)}</Col>
          </Row>
        ))}
      </div>
    </>
  );
}
